<template>
    <section class="flex flex-col gap-6">
        <!-- Loading skeleton -->
        <GoalsSkeleton v-if="isLoading" />

        <div v-else class="flex flex-col gap-4">
            <div class="relative">
                <div v-if="showAddGoalInline" class="hidden md:flex absolute right-0 top-0 z-10 h-10 items-center">
                    <BaseButton theme="primary" size="sm" @click="handleAddGoal(addGoalInlineData, 'tab_actions')">
                        {{ activeTab === "for-your-team" ? "Add team goal" : "Add goal" }}
                    </BaseButton>
                </div>
                <BaseTabs v-model="activeTab" :tab-ids="visibleTabIds" :labels="tabLabels" list-class="w-fit" content-class="pt-6">
                    <!-- For you tab -->
                    <template #content-for-you>
                        <div v-if="showAddGoalInline" class="md:hidden flex justify-end mb-4">
                            <BaseButton theme="primary" size="sm" @click="handleAddGoal(addGoalInlineData, 'tab_actions')"> Add goal </BaseButton>
                        </div>
                        <PersonalGoalsBoard
                            :suggestions="personalSuggestions"
                            :journey-goals="prioritizedPersonalGoals"
                            :selected-goal-id="selectedGoalId"
                            :goal-is-loading="personalGoalIsLoading"
                            :goal-is-discuss-loading="goalIsDiscussLoading"
                            :max-prioritized-goals="MAX_PRIORITIZED_GOALS"
                            :hovered-resource-goal-id="hoveredResourceGoalId"
                            @select="(id: string, zone: JourneysSection) => handleGoalSelect(id, zone)"
                            @drop="handlePersonalDrop"
                            @explain-suggestions="handleExplainSuggestions"
                            @explain-priority="handleExplainPriority"
                            @drag-start="handleBoardDragStart"
                            @discuss="handleDiscussGoal"
                            @add-goal="handleAddGoalFromPersonalBoard">
                            <template #below-priority-goals>
                                <SuggestedResourcesSection
                                    v-if="suggestedResourcesEnabled && prioritizedPersonalGoals.length > 0"
                                    :goals="prioritizedPersonalGoals"
                                    @hover-goal="hoveredResourceGoalId = $event" />
                            </template>
                        </PersonalGoalsBoard>
                    </template>

                    <!-- For your team tab -->
                    <template #content-for-your-team>
                        <div v-if="showAddGoalInline" class="md:hidden flex justify-end mb-4">
                            <BaseButton theme="primary" size="sm" @click="handleAddGoal(addGoalInlineData, 'tab_actions')"> Add team goal </BaseButton>
                        </div>
                        <TeamGoalsBoard
                            ref="teamGoalsBoardRef"
                            :suggestions="allTeamSuggestions"
                            :lanes="teamLanes"
                            :selected-goal-id="selectedGoalId"
                            :goal-is-loading="teamGoalIsLoading"
                            @select="(id: string, zone: JourneysSection) => handleGoalSelect(id, zone)"
                            @drop-on-lane="handleDropOnLane"
                            @explain-suggestions="handleExplainSuggestions"
                            @drag-start="handleBoardDragStart"
                            @assign="handleTeamGoalAssignClicked"
                            @discuss="handleDiscussGoal" />
                    </template>

                    <!-- Completed tab -->
                    <template #content-completed>
                        <div class="flex flex-col gap-4">
                            <GoalsSkeleton v-if="showCompletedSkeleton" :count="4" />
                            <p v-else-if="completedGoals.length === 0" class="body-small text-ink-muted">After you mark goals as completed, they'll show up here.</p>
                            <TransitionGroup v-else tag="div" name="goal-card" class="relative goals-grid">
                                <GoalCard
                                    v-for="goal in completedGoals"
                                    :key="goal.id"
                                    :goal="goal"
                                    :is-loading="completedGoalsComposable.goalIsLoading(goal.id)"
                                    :is-selected="goal.id === selectedGoalId"
                                    @select="handleGoalSelect($event.id, 'completed')" />
                            </TransitionGroup>
                            <div v-if="completedGoalsComposable.hasMore.value" class="flex justify-center">
                                <BaseButton theme="tertiary" size="sm" :disabled="completedGoalsComposable.isFetching.value" @click="completedGoalsComposable.loadMore()"
                                    >Load more</BaseButton
                                >
                            </div>
                        </div>
                    </template>
                </BaseTabs>
            </div>
        </div>

        <GoalDetailSidebar
            :goal="selectedGoal"
            :current-tab="activeTab"
            :hide-footer="selectedGoalIsCompleted"
            :disable-actions="selectedGoalIsCompleted"
            :can-prioritize="!selectedGoal?.team_member_goal && prioritizedPersonalGoals.length < MAX_PRIORITIZED_GOALS"
            @close="selectedGoalId = null"
            @action-added="handleSidebarActionAdded"
            @goal-completed="handleSidebarGoalCompleted"
            @goal-updated="handleSidebarGoalUpdated"
            @goal-assigned="handleSidebarGoalAssigned"
            @toggle-priority="handleSidebarTogglePriority"
            @track-suggestion="handleSidebarTrackSuggestion"
            @edit="handleSidebarEdit"
            @goal-deleted="handleSidebarGoalDeleted" />
        <GoalEditSidebar
            v-model:open="editDialogOpen"
            :initial-data="editingGoalData"
            :is-submitting="isSubmitting"
            :require-connection="requireConnectionForEdit"
            :fixed-connection="fixedConnectionForEdit"
            :can-prioritize="!editingGoalData.id && !editingGoalData.team_member_goal && prioritizedPersonalGoals.length < MAX_PRIORITIZED_GOALS"
            @submit="handleGoalSubmit"
            @close="handleEditSidebarClose" />

        <PriorityGoalsInfoModal v-model:open="showPriorityGoalsInfo" />
        <NadiaSuggestionsInfoModal v-model:open="showNadiaSuggestionsInfo" />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { type GoalUpdateRequest } from "~vue/api/generated/goals/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { useListGoals } from "~vue/composables/useListGoals";
import { type Goal, type GoalConnection, type GoalCreateEditData, type GoalCreateRequest } from "~vue/types/goals";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { sortGoalsByDateDesc } from "~vue/utils/goals";
import { computed, onMounted, ref, watch } from "vue";

import GoalCard from "./GoalCard.vue";
import GoalDetailSidebar from "./GoalDetailSidebar.vue";
import GoalEditSidebar from "./GoalEditSidebar.vue";
import GoalsSkeleton from "./GoalsSkeleton.vue";
import NadiaSuggestionsInfoModal from "./NadiaSuggestionsInfoModal.vue";
import PersonalGoalsBoard from "./PersonalGoalsBoard.vue";
import PriorityGoalsInfoModal from "./PriorityGoalsInfoModal.vue";
import SuggestedResourcesSection from "./SuggestedResourcesSection.vue";
import TeamGoalsBoard from "./TeamGoalsBoard.vue";
import {
    type DiscussWithNadiaClickedExtraData,
    type GoalDroppedExtraData,
    type GoalPrioritizedExtraData,
    type GoalTrackedExtraData,
    JourneysAmplitudeEventNames,
    type JourneysSection,
    type JourneysTab,
    logJourneysUserInteraction,
} from "./utils";

const props = defineProps<{ isManager: boolean | null; suggestedResourcesEnabled: boolean }>();

const showAddGoalInline = computed(() => {
    if (activeTab.value === "for-you") {
        return true;
    }
    if (activeTab.value === "for-your-team") {
        return props.isManager === true;
    }
    return false;
});
const addGoalInlineData = computed<Partial<GoalCreateEditData>>(() => (activeTab.value === "for-your-team" ? { team_member_goal: true } : {}));

const personalGoalsComposable = useListGoals({ status: "active", forTeamMember: false, source: "user", pageSize: null });
const teamGoalsComposable = useListGoals({ status: "active", forTeamMember: true, source: "user", pageSize: null });
const extractedGoalsComposable = useListGoals({ status: "active", forTeamMember: false, source: "extracted", pageSize: null });
const extractedTeamGoalsComposable = useListGoals({ status: "active", forTeamMember: true, source: "extracted", pageSize: null });
const completedGoalsComposable = useListGoals({ status: "completed", forTeamMember: null, pageSize: 10 });

const personalGoals = personalGoalsComposable.goals;
const teamGoals = teamGoalsComposable.goals;
const extractedGoals = extractedGoalsComposable.goals;
const extractedTeamGoals = extractedTeamGoalsComposable.goals;
const completedGoals = completedGoalsComposable.goals;
const teamGoalsBoardRef = ref<InstanceType<typeof TeamGoalsBoard> | null>(null);

const selectedGoalId = ref<string | null>(null);
const hoveredResourceGoalId = ref<string | null>(null);

function handleGoalSelect(goalId: string, section: JourneysSection) {
    editDialogOpen.value = false;
    selectedGoalId.value = goalId;
    logJourneysUserInteraction(
        JourneysAmplitudeEventNames.GOAL_CARD_CLICKED,
        {
            goal_id: goalId,
            section,
        },
        activeTab.value,
    );
}

function handleTeamGoalAssignClicked(goalId: string) {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_ASSIGN_CLICKED, { goal_id: goalId }, activeTab.value);
    selectedGoalId.value = goalId;
}

const discussGoalId = ref("");
const {
    execute: fetchDiscussSession,
    isFetching: isFetchingDiscussSession,
    data: discussSessionData,
    error: discussSessionError,
} = useFetch(
    computed(() => `/goals/${discussGoalId.value}/chat/`),
    { immediate: false },
).json<{ coaching_session_url?: string }>();

async function handleDiscussGoal(goalId: string) {
    const discussData: DiscussWithNadiaClickedExtraData = { goal_id: goalId, source: "card" };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.DISCUSS_WITH_NADIA_CLICKED, discussData, activeTab.value);
    discussGoalId.value = goalId;
    await fetchDiscussSession();
    if (discussSessionError.value) {
        return;
    }
    const url = discussSessionData.value?.coaching_session_url;
    if (url) {
        globalThis.location.href = url;
    }
}

async function handleSidebarTrackSuggestion(goalId: string) {
    const trackData: GoalTrackedExtraData = { goal_id: goalId, method: "button", to_section: "other_goals", is_team_goal: false };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_TRACKED, trackData, activeTab.value);
    await trackExtractedGoal(goalId, false, false);
}

const selectedGoal = computed(
    () =>
        personalGoals.value.find((g) => g.id === selectedGoalId.value) ??
        teamGoals.value.find((g) => g.id === selectedGoalId.value) ??
        extractedGoals.value.find((g) => g.id === selectedGoalId.value) ??
        extractedTeamGoals.value.find((g) => g.id === selectedGoalId.value) ??
        completedGoals.value.find((g) => g.id === selectedGoalId.value) ??
        null,
);
const selectedGoalIsCompleted = computed(() => completedGoals.value.some((g) => g.id === selectedGoalId.value));

const MAX_PRIORITIZED_GOALS = 3;

// Tracks goal IDs that originated as Nadia suggestions and have since been confirmed via tracking.
// Used to re-bucket them under "Nadia's suggestions" if the user un-prioritizes them again.

const prioritizedPersonalGoals = computed(() => personalGoals.value.filter((g) => g.prioritized_at !== null).slice(0, MAX_PRIORITIZED_GOALS));

const unprioritizedPersonalGoals = computed(() => personalGoals.value.filter((g) => g.prioritized_at === null));

// Merged stack for the For You "Your Goals" column (extracted + tracked-but-unprioritized);
// PersonalGoalsBoard renders it newest-first via sortGoalsByDateDesc.
const personalSuggestions = computed(() => [...extractedGoals.value, ...unprioritizedPersonalGoals.value]);

function personalGoalIsLoading(goalId: string): boolean {
    return personalGoalsComposable.goalIsLoading(goalId) || extractedGoalsComposable.goalIsLoading(goalId);
}

function goalIsDiscussLoading(goalId: string): boolean {
    return isFetchingDiscussSession.value && discussGoalId.value === goalId;
}

function teamGoalIsLoading(goalId: string): boolean {
    return teamGoalsComposable.goalIsLoading(goalId) || extractedTeamGoalsComposable.goalIsLoading(goalId);
}

const showCompletedSkeleton = computed(() => completedGoalsComposable.isFetching.value && completedGoals.value.length === 0);

const mutatingGoalId = ref("");
const goalUpdatePayload = ref<GoalUpdateRequest>({});
const {
    execute: executeGoalUpdate,
    data: updatedGoal,
    error: goalUpdateError,
} = useFetch(
    computed(() => `/v1/goals/${mutatingGoalId.value}`),
    { immediate: false },
)
    .patch(goalUpdatePayload)
    .json<Goal>();

const goalCreatePayload = ref<GoalCreateRequest>({ title: "" });
const { execute: executeGoalCreate, data: createdGoal } = useFetch("/v1/goals/", { immediate: false }).post(goalCreatePayload).json<Goal>();

async function updateGoal(id: string, payload: GoalUpdateRequest) {
    mutatingGoalId.value = id;
    goalUpdatePayload.value = payload;
    await executeGoalUpdate();
}

async function createGoal(payload: GoalCreateRequest) {
    goalCreatePayload.value = payload;
    await executeGoalCreate();
}

async function handleSidebarActionAdded(goalId: string) {
    const list = personalGoals.value.some((g) => g.id === goalId) ? personalGoalsComposable : teamGoalsComposable;
    await list.refreshGoal(goalId);
}

async function handleSidebarGoalCompleted(goalId: string) {
    const list = personalGoals.value.some((g) => g.id === goalId) ? personalGoalsComposable : teamGoalsComposable;
    list.removeGoal(goalId);
    selectedGoalId.value = null;
    await completedGoalsComposable.refreshGoal(goalId);
}

function handleSidebarGoalDeleted(goalId: string) {
    personalGoalsComposable.removeGoal(goalId);
    teamGoalsComposable.removeGoal(goalId);
    extractedGoalsComposable.removeGoal(goalId);
    extractedTeamGoalsComposable.removeGoal(goalId);
    selectedGoalId.value = null;
}

async function handleSidebarGoalUpdated(goalId: string) {
    extractedGoalsComposable.removeGoal(goalId);
    extractedTeamGoalsComposable.removeGoal(goalId);
    const list = personalGoals.value.some((g) => g.id === goalId) ? personalGoalsComposable : teamGoalsComposable;
    await list.refreshGoal(goalId);
    const connectionId = teamGoals.value.find((g) => g.id === goalId)?.connection?.id;
    if (connectionId) {
        teamGoalsBoardRef.value?.expandLane(connectionId);
    }
}

async function handleSidebarTogglePriority(goalId: string) {
    const isExtracted = extractedGoals.value.some((g) => g.id === goalId);
    const personalGoal = personalGoals.value.find((g) => g.id === goalId);
    const isPrioritized = !isExtracted && (personalGoal?.prioritized_at ?? null) !== null;
    if (!isExtracted && !personalGoal) {
        return;
    }
    if (!isPrioritized && !isExtracted && prioritizedPersonalGoals.value.length >= MAX_PRIORITIZED_GOALS) {
        return;
    }
    const newState = !isPrioritized ? "prioritized" : "unprioritized";
    let priorityUpdateSucceeded = false;
    if (isExtracted) {
        priorityUpdateSucceeded = await handleSidebarTrackAsPriority(goalId);
    } else {
        priorityUpdateSucceeded = await patchGoalPriority(goalId, !isPrioritized);
    }
    if (priorityUpdateSucceeded) {
        const prioritizedData: GoalPrioritizedExtraData = { goal_id: goalId, method: "button", new_state: newState };
        logJourneysUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, prioritizedData, activeTab.value);
    }
}

async function handleSidebarTrackAsPriority(goalId: string): Promise<boolean> {
    if (!extractedGoals.value.some((g) => g.id === goalId)) {
        return false;
    }
    const trackData: GoalTrackedExtraData = { goal_id: goalId, method: "priority_button", to_section: "priority_goals", is_team_goal: false };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_TRACKED, trackData, activeTab.value);
    return trackExtractedGoal(goalId, true, false);
}

async function handleSidebarGoalAssigned(goalId: string) {
    personalGoalsComposable.removeGoal(goalId);
    extractedGoalsComposable.removeGoal(goalId);
    extractedTeamGoalsComposable.removeGoal(goalId);
    selectedGoalId.value = null;
    await teamGoalsComposable.refreshGoal(goalId);
    const connectionId = teamGoals.value.find((g) => g.id === goalId)?.connection?.id;
    if (connectionId) {
        teamGoalsBoardRef.value?.expandLane(connectionId);
    }
    activeTab.value = "for-your-team";
}

const editDialogOpen = ref(false);
const editingGoalData = ref<Partial<GoalCreateEditData>>({});
const requireConnectionForEdit = ref(false);
const fixedConnectionForEdit = ref<GoalConnection | null>(null);

function handleEditSidebarClose() {
    if (editingGoalData.value.id) {
        selectedGoalId.value = editingGoalData.value.id;
    }
}

function handleSidebarEdit(goalId: string) {
    const goal = selectedGoal.value;
    if (!goal || goal.id !== goalId) {
        return;
    }
    editingGoalData.value = {
        id: goal.id,
        title: goal.title,
        description: goal.description ?? "",
        goal_type: goal.goal_type,
        team_member_goal: goal.team_member_goal,
        connection_id: goal.connection_id ?? null,
        connection: goal.connection ?? null,
    };
    requireConnectionForEdit.value = false;
    fixedConnectionForEdit.value = goal.connection ?? null;
    selectedGoalId.value = null;
    editDialogOpen.value = true;
}

function handleAddGoal(initialData: Partial<GoalCreateEditData>, source: "other_goals_section" | "tab_actions" | "empty_state" = "other_goals_section") {
    editingGoalData.value = initialData;
    requireConnectionForEdit.value = initialData.team_member_goal === true;
    fixedConnectionForEdit.value = null;
    editDialogOpen.value = true;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.ADD_GOAL_CLICKED, { source }, activeTab.value);
}

function handleAddGoalFromPersonalBoard(target: "priority_goals" | "other_goals") {
    const canPrioritizeNew = target === "priority_goals" && prioritizedPersonalGoals.value.length < MAX_PRIORITIZED_GOALS;
    handleAddGoal({ is_prioritized: canPrioritizeNew }, "empty_state");
}

onMounted(async () => {
    await Promise.all([
        personalGoalsComposable.fetch(),
        teamGoalsComposable.fetch(),
        extractedGoalsComposable.fetch(),
        extractedTeamGoalsComposable.fetch(),
        completedGoalsComposable.fetch(),
    ]);
    activeTab.value = "for-you";
});

const tabLabels: Record<JourneysTab, string> = { "for-you": "For you", "for-your-team": "For your team", completed: "Completed" };
const visibleTabIds = computed<JourneysTab[]>(() => {
    if (props.isManager === true) {
        return ["for-you", "for-your-team", "completed"];
    }
    return ["for-you", "completed"];
});
const activeTab = ref<JourneysTab | undefined>(undefined);

watch(visibleTabIds, (ids) => {
    if (activeTab.value && !ids.includes(activeTab.value)) {
        activeTab.value = ids[0];
    }
});

function closeSidebars() {
    selectedGoalId.value = null;
    editDialogOpen.value = false;
}

function handleBoardDragStart(goalId: string, fromSection: JourneysSection) {
    closeSidebars();
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DRAGGED, { goal_id: goalId, from_section: fromSection }, activeTab.value);
}

// Track an extracted suggestion into the user/team's goal list with a given priority state.
// Works for both personal (team_member_goal=false) and team (team_member_goal=true) extracted goals.
async function trackExtractedGoal(goalId: string, isPrioritized: boolean, isTeam: boolean): Promise<boolean> {
    const sourceComposable = isTeam ? extractedTeamGoalsComposable : extractedGoalsComposable;
    const targetComposable = isTeam ? teamGoalsComposable : personalGoalsComposable;
    const sourceList = isTeam ? extractedTeamGoals : extractedGoals;
    const original = sourceList.value.find((g) => g.id === goalId);
    if (!original) {
        return false;
    }
    sourceComposable.removeGoal(goalId);
    await targetComposable.refreshGoal({ ...original, source: "confirmed", team_member_goal: isTeam, prioritized_at: isPrioritized ? new Date().toISOString() : null });
    await updateGoal(goalId, { confirm_goal: true, team_member_goal: isTeam, is_prioritized: isPrioritized });
    if (!goalUpdateError.value && updatedGoal.value) {
        await targetComposable.refreshGoal(updatedGoal.value.id);
        return true;
    } else {
        targetComposable.removeGoal(goalId);
        await sourceComposable.refreshGoal(original);
        return false;
    }
}

async function handlePersonalDrop(goalId: string, from: JourneysSection, to: JourneysSection) {
    const droppedData: GoalDroppedExtraData = { goal_id: goalId, from_section: from, to_section: to };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DROPPED, droppedData, activeTab.value);
    if (to === "priority_goals") {
        const extracted = extractedGoals.value.find((g) => g.id === goalId);
        if (extracted) {
            const trackData: GoalTrackedExtraData = { goal_id: goalId, method: "drag", to_section: to, is_team_goal: false };
            logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_TRACKED, trackData, activeTab.value);
            const priorityUpdateSucceeded = await trackExtractedGoal(goalId, true, false);
            if (priorityUpdateSucceeded) {
                const prioritizedData: GoalPrioritizedExtraData = { goal_id: goalId, method: "drag", new_state: "prioritized" };
                logJourneysUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, prioritizedData, activeTab.value);
            }
        } else {
            const priorityUpdateSucceeded = await patchGoalPriority(goalId, true);
            if (priorityUpdateSucceeded) {
                const prioritizedData: GoalPrioritizedExtraData = { goal_id: goalId, method: "drag", new_state: "prioritized" };
                logJourneysUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, prioritizedData, activeTab.value);
            }
        }
    } else if (to === "other_goals" && from === "priority_goals") {
        const priorityUpdateSucceeded = await patchGoalPriority(goalId, false);
        if (priorityUpdateSucceeded) {
            const prioritizedData: GoalPrioritizedExtraData = { goal_id: goalId, method: "drag", new_state: "unprioritized" };
            logJourneysUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, prioritizedData, activeTab.value);
        }
    } else if (to === "other_goals" && from === "suggestions") {
        const trackData: GoalTrackedExtraData = { goal_id: goalId, method: "drag", to_section: to, is_team_goal: false };
        logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_TRACKED, trackData, activeTab.value);
        await trackExtractedGoal(goalId, false, false);
    }
}

async function handleDropOnLane(goalId: string, connectionId: string, fromSection: JourneysSection, fromConnectionId?: string | null) {
    const droppedData: GoalDroppedExtraData = {
        goal_id: goalId,
        from_section: fromSection,
        to_section: "team_lane",
        connection_id: connectionId,
        from_connection_id: fromConnectionId ?? null,
    };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DROPPED, droppedData, activeTab.value);
    await assignGoalToLane(goalId, connectionId);
}

async function assignGoalToLane(goalId: string, connectionId: string) {
    const isExtracted = extractedTeamGoals.value.some((g) => g.id === goalId);
    const original = isExtracted ? extractedTeamGoals.value.find((g) => g.id === goalId)! : teamGoals.value.find((g) => g.id === goalId);
    if (!original) {
        return;
    }

    const connection = teamLanes.value.find((l) => l.connection.id === connectionId)?.connection ?? null;

    if (isExtracted) {
        extractedTeamGoalsComposable.removeGoal(goalId);
    }
    await teamGoalsComposable.refreshGoal({ ...original, source: "confirmed", team_member_goal: true, connection_id: connectionId, connection });

    const payload = isExtracted ? { confirm_goal: true, team_member_goal: true, connection_id: connectionId } : { connection_id: connectionId };
    await updateGoal(goalId, payload);

    if (!goalUpdateError.value && updatedGoal.value) {
        await teamGoalsComposable.refreshGoal(updatedGoal.value.id);
    } else if (isExtracted) {
        teamGoalsComposable.removeGoal(goalId);
        await extractedTeamGoalsComposable.refreshGoal(original);
    } else {
        await teamGoalsComposable.refreshGoal(original);
    }
}

const showPriorityGoalsInfo = ref(false);
const showNadiaSuggestionsInfo = ref(false);

function handleExplainSuggestions() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.NADIA_SUGGESTIONS_INFO_OPENED, {}, activeTab.value);
    showNadiaSuggestionsInfo.value = true;
}

function handleExplainPriority() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.PRIORITY_GOALS_INFO_OPENED, {}, activeTab.value);
    showPriorityGoalsInfo.value = true;
}

watch(activeTab, (_newTab, oldTab) => {
    if (oldTab !== undefined && _newTab !== undefined) {
        closeSidebars();
        logJourneysUserInteraction(JourneysAmplitudeEventNames.TAB_CLICKED, {}, _newTab);
    }
});

async function patchGoalPriority(goalId: string, isPrioritized: boolean): Promise<boolean> {
    const original = personalGoals.value.find((g) => g.id === goalId);
    if (!original) {
        return false;
    }

    // Optimistic update: move the goal immediately so there's no flicker
    await personalGoalsComposable.refreshGoal({ ...original, prioritized_at: isPrioritized ? new Date().toISOString() : null });

    await updateGoal(goalId, { is_prioritized: isPrioritized });

    if (!goalUpdateError.value && updatedGoal.value) {
        // Re-fetch by ID so the goal retains all fields (e.g. actions) not included in the PATCH response
        await personalGoalsComposable.refreshGoal(updatedGoal.value.id);
        return true;
    } else {
        // Revert on failure
        await personalGoalsComposable.refreshGoal(original);
        return false;
    }
}

// Per-connection journey boards: each direct report gets their own suggestions + journey.
// Suggestions = extracted-for-this-report + unprioritized-confirmed-for-this-report.
// Journey = prioritized confirmed goals for this report (cap MAX_PRIORITIZED_GOALS).
const teamConnectionBoards = computed(() => {
    const byId = new Map<string, { connection: GoalConnection; suggestions: Goal[]; journeyGoals: Goal[] }>();
    function ensure(connection: GoalConnection) {
        if (!byId.has(connection.id)) {
            byId.set(connection.id, { connection, suggestions: [], journeyGoals: [] });
        }
        return byId.get(connection.id)!;
    }
    for (const goal of extractedTeamGoals.value) {
        if (!goal.connection) {
            continue;
        }
        ensure(goal.connection).journeyGoals.push(goal);
    }
    for (const goal of teamGoals.value) {
        if (!goal.connection) {
            continue;
        }
        ensure(goal.connection).journeyGoals.push(goal);
    }
    return [...byId.values()].sort((a, b) => (a.connection.name ?? "").localeCompare(b.connection.name ?? ""));
});

// Merged team suggestions stack: every unprioritized team goal (extracted + confirmed), regardless of connection.
const allTeamSuggestions = computed(() => {
    const unassignedExtracted = extractedTeamGoals.value.filter((g) => !g.connection);
    const unassignedConfirmed = teamGoals.value.filter((g) => !g.connection);
    return sortGoalsByDateDesc([...unassignedExtracted, ...unassignedConfirmed]);
});

// Lanes: connections with at least one assigned (prioritized) team goal.
const teamLanes = computed(() =>
    teamConnectionBoards.value
        .filter((b) => b.journeyGoals.length > 0)
        .map((b) => ({ connection: b.connection, journeyGoals: sortGoalsByDateDesc(b.journeyGoals) }))
        .sort((a, b) => (a.connection.name ?? "").localeCompare(b.connection.name ?? "")),
);

const isLoading = computed(
    () =>
        personalGoalsComposable.isFetching.value ||
        teamGoalsComposable.isFetching.value ||
        extractedGoalsComposable.isFetching.value ||
        extractedTeamGoalsComposable.isFetching.value,
);

const creatingGoal = ref(false);
const updatingGoal = ref(false);
const isSubmitting = computed(() => creatingGoal.value || updatingGoal.value);

function switchToGoalTab(goal: Goal) {
    activeTab.value = goal.team_member_goal ? "for-your-team" : "for-you";
}

async function handleGoalSubmit(data: GoalCreateEditData) {
    if (data.id) {
        updatingGoal.value = true;
        await updateGoal(data.id, {
            title: data.title,
            description: data.description,
            goal_type: data.goal_type,
            team_member_goal: data.team_member_goal,
            connection_id: data.connection_id ?? null,
        });
        updatingGoal.value = false;
        if (updatedGoal.value) {
            const targetList = updatedGoal.value.team_member_goal ? teamGoalsComposable : personalGoalsComposable;
            const otherList = updatedGoal.value.team_member_goal ? personalGoalsComposable : teamGoalsComposable;
            otherList.removeGoal(updatedGoal.value.id);
            await targetList.refreshGoal(updatedGoal.value.id);
            switchToGoalTab(updatedGoal.value);
            selectedGoalId.value = updatedGoal.value.id;
            logJourneysUserInteraction(
                JourneysAmplitudeEventNames.GOAL_EDITED,
                { goal_id: data.id, goal_type: data.goal_type, has_description: !!data.description?.trim() },
                activeTab.value,
            );
        }
    } else {
        creatingGoal.value = true;
        await createGoal({
            title: data.title,
            description: data.description,
            goal_type: data.goal_type,
            team_member_goal: data.team_member_goal,
            connection_id: data.connection_id ?? null,
            is_prioritized: data.is_prioritized,
        });
        creatingGoal.value = false;
        if (createdGoal.value) {
            const list = createdGoal.value.team_member_goal ? teamGoalsComposable : personalGoalsComposable;
            await list.refreshGoal(createdGoal.value.id);
            switchToGoalTab(createdGoal.value);
            selectedGoalId.value = createdGoal.value.id;
            logJourneysUserInteraction(
                JourneysAmplitudeEventNames.GOAL_CREATED,
                { goal_type: data.goal_type, is_team_goal: data.team_member_goal ?? false, has_description: !!data.description?.trim() },
                activeTab.value,
            );
        }
    }
}

async function refreshGoalFromExtracted(goal: Goal) {
    const list = goal.team_member_goal ? teamGoalsComposable : personalGoalsComposable;
    await list.refreshGoal(goal.id);
}

defineExpose({ refreshGoalFromExtracted });
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

/* Responsive grid: 1 col on small/medium, 3 col on large */
.goals-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 1024px) {
    .goals-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.goal-card-enter-active {
    transition: opacity 250ms ease;
}

.goal-card-enter-from {
    opacity: 0;
}

.goal-card-leave-active {
    transition: opacity 200ms ease;
    position: absolute;
    /* Stay within the grid column width instead of stretching to 100% */
    width: 0;
    overflow: hidden;
}

.goal-card-leave-to {
    opacity: 0;
}

.goal-card-move {
    transition: transform 300ms ease;
}

/* For your team page — slides up */
.suggestion-column-up-enter-active,
.suggestion-column-up-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}
.suggestion-column-up-enter-from,
.suggestion-column-up-leave-to {
    opacity: 0;
    transform: translateY(-12px);
}
</style>
